import React from 'react';
import { cn } from '@/lib/utils';
import type { DataValue } from '@glueapp/graph-types';

interface Props {
    value: DataValue;
    executionDecision?: boolean;
    onClose: () => void;
}

function formatValue(value: DataValue): string {
    if (value === null || value === undefined) return 'null';
    switch (typeof value) {
        case 'string':
            return `"${value}"`;
        case 'number':
        case 'bigint':
        case 'boolean':
            return String(value);
        case 'object':
            return JSON.stringify(value, null, 2);
        default:
            return String(value);
    }
}

export const OutputValueOverlay: React.FC<Props> = ({ value, executionDecision, onClose }) => {
    return (
        <div className="flex flex-col gap-1.5 text-sm">
            <div className="flex items-center justify-between gap-4">
                {executionDecision !== undefined ? (
                    <div className="flex items-center gap-2 text-xs font-medium">
                        {executionDecision ? (
                            <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                                Executed
                            </span>
                        ) : (
                            <span className="flex items-center gap-1 text-neutral-400 dark:text-neutral-500">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <rect x="6" y="6" width="12" height="12" />
                                </svg>
                                Skipped
                            </span>
                        )}
                    </div>
                ) : (
                    <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Output</span>
                )}
                <button
                    onClick={e => {
                        e.stopPropagation();
                        onClose();
                    }}
                    aria-label="Close"
                    className={cn(
                        'p-0.5 rounded transition-colors -mr-0.5',
                        'text-neutral-400 dark:text-neutral-500',
                        'hover:bg-neutral-200/50 dark:hover:bg-neutral-700/50',
                        'hover:text-neutral-600 dark:hover:text-neutral-300',
                    )}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </button>
            </div>
            <div className="h-px bg-neutral-200/60 dark:bg-neutral-700/40 -mx-3" />
            <pre className="whitespace-pre-wrap break-words text-neutral-800 dark:text-neutral-200 max-h-[400px] overflow-y-auto overscroll-contain text-xs font-mono leading-relaxed">
                {formatValue(value)}
            </pre>
        </div>
    );
};
